import { produce } from 'immer';
import { FieldTypes } from '../helpers/constants';
import { mergeDeep } from '../helpers/utility';
import {
  FORM_EDITOR_INIT,
  FORM_UPDATE_FIELD_VALUES,
  FORM_UPDATE_FORMULA_VALUES,
  FORM_UPDATE_NAME,
  FORM_UPDATE_TAGS,
  FORM_UPDATE_TEMPLATE_VERSION,
  FORM_GET_TEMPLATE_SUCCESS,
  FORM_GET_PUBLICSHARE_TEMPLATE_SUCCESS,
  FORM_COMPLETE,
  FORM_REOPEN_REQUEST,
  FORM_REOPEN_SUCCESS,
  FORM_REOPEN_OPEN_WARNING,
  FORM_REOPEN_CLOSE_WARNING,
  FORM_REOPEN_FAILURE,
  FORM_CLOSE,
  FORM_GET_FORM_REQUEST,
  FORM_GET_FORM_SUCCESS,
  FORM_GET_FORM_FAILURE,
  FORM_GET_PUBLICSHARE_REQUEST,
  FORM_GET_PUBLICSHARE_FAILURE,
  FORM_GET_PUBLICSHARE_FORM_SUCCESS,
  FORM_SAVE_REQUEST,
  FORM_SAVE_FAILURE,
  FORM_DUPLICATE_REQUEST,
  FORM_DUPLICATE_SUCCESS,
  FORM_DUPLICATE_FAILURE,
  FORM_TRANSFER_REQUEST,
  FORM_TRANSFER_SUCCESS,
  FORM_TRANSFER_FAILURE,
  FORM_GET_TEMPLATE_REQUEST,
  FORM_GET_TEMPLATE_FAILURE,
  FORM_COMPLETE_FIELDS_REQUIRED,
  FORM_CLOSE_ERROR,
  FORM_CLOSE_OUTDATED,
  FORM_COMPLETE_REQUEST,
  FORM_COMPLETE_SUCCESS,
  FORMS_DELETE_FAILURE,
  FORM_METADATA_REQUEST,
  FORM_METADATA_SUCCESS,
  FORM_METADATA_FAILURE,
  FORMS_DELETE_REQUEST,
  FORMS_DELETE_SUCCESS,
  FORM_GET_PUBLICSHARE_IMAGE_URL_REQUEST,
  FORM_GET_PUBLICSHARE_IMAGE_URL_SUCCESS,
  FORM_GET_PUBLICSHARE_IMAGE_URL_FAILURE,
  FORM_SAVE_SUCCESS,
  FORM_RECOVER,
  UPDATE_SESSION_SIGNATURE,
  FORM_SET_HIGHLIGHT,
  FORM_RESET_HIGHLIGHT,
  FORM_SET_VIEW,
  FORM_GET_PUBLICSHARE_FILE_URLS_SUCCESS,
  UPDATE_SAVED_PUBLIC_FORM,
  UPDATE_SAVED_PUBLIC_FORM_BLOB,
  PUBLIC_FORM_SAVE_REQUEST,
  PUBLIC_FORM_SAVE_SUCCESS,
  PUBLIC_FORM_SAVE_FAILURE,
  FORM_UPDATE_FIELD_HIGHLIGHT,
  FORM_INVALID_FIELD_VALUES,
  FORM_UPDATE_VALIDATION_FORMULA_RESULTS
} from './actions';
import { FORM_HIPAA_CLOSE, FORM_HIPAA_INIT } from '../settings/hipaa/actions/submitActions';
import { FormEditorAction, FormEditorState } from './formeditor-types';
import {
  updateFieldValues,
  updateFormulaValues,
  updateName,
  updateTemplateVersion,
  createForm,
  completeForm,
  reopenFormSuccess,
  closeForm,
  updateTags,
  initForm,
  formSaveSuccess,
  updateSavedPublicFormState,
  updateSavedPublicFormBlobState,
  updateValidationResults,
  updateFieldHighlight
} from './updaters';
import { sendAFormReduxReducer } from '../sendaform/reduxReducer';

const formeditor = (state: FormEditorState = {} as FormEditorState, action: FormEditorAction) => {
  if (!action) return state;
  try {
    return produce(state, (draft: FormEditorState) => reduce(draft, action));
  } catch (e) {
    console.log(e);
  }
};

function reduce(state = {} as FormEditorState, action) {
  switch (action.type) {
    case FORM_EDITOR_INIT:
      return initForm(state, action.payload);

    case FORM_UPDATE_FIELD_VALUES:
      state.isUnsaved = action.payload.isUnsaved;
      return updateFieldValues(
        state,
        action.payload.list,
        action.payload.userFullName,
        action.payload.userEmail,
        action.payload.dateTimeFormat,
        action.payload.fieldValidationsFF
      );

    case FORM_UPDATE_FORMULA_VALUES:
      return updateFormulaValues(state, action.payload.list);

    case FORM_UPDATE_VALIDATION_FORMULA_RESULTS:
      return updateValidationResults(state, action.payload.list);

    case FORM_UPDATE_FIELD_HIGHLIGHT:
      return updateFieldHighlight(state, action.payload);

    case FORM_UPDATE_NAME:
      return updateName(state, action.payload);

    case FORM_UPDATE_TAGS:
      return updateTags(state, action.payload);

    case FORM_UPDATE_TEMPLATE_VERSION:
      return updateTemplateVersion(state, action.payload);

    case FORM_GET_TEMPLATE_SUCCESS:
    case FORM_GET_PUBLICSHARE_TEMPLATE_SUCCESS:
      return createForm({ ...state, ...action.payload }, action.userId);

    case FORM_COMPLETE:
      return completeForm(state, action.payload.location);

    case FORM_REOPEN_SUCCESS:
      return reopenFormSuccess(state, action.payload);

    case FORM_REOPEN_OPEN_WARNING:
      state.reopenWarningOpen = true;
      return state;

    case FORM_REOPEN_CLOSE_WARNING:
      state.reopenWarningOpen = false;
      return state;

    case FORM_CLOSE:
      return closeForm(state);

    case FORM_GET_FORM_REQUEST:
    case FORM_GET_FORM_SUCCESS:
    case FORM_GET_FORM_FAILURE:
    case FORM_GET_PUBLICSHARE_REQUEST:
    case FORM_GET_PUBLICSHARE_FAILURE:
    case FORM_GET_PUBLICSHARE_FORM_SUCCESS:
    case FORM_GET_PUBLICSHARE_FILE_URLS_SUCCESS:
    case FORM_SAVE_REQUEST:
    case FORM_SAVE_FAILURE:
    case FORM_REOPEN_REQUEST:
    case FORM_REOPEN_FAILURE:
    case FORM_DUPLICATE_REQUEST:
    case FORM_DUPLICATE_SUCCESS:
    case FORM_DUPLICATE_FAILURE:
    case FORM_TRANSFER_REQUEST:
    case FORM_TRANSFER_SUCCESS:
    case FORM_TRANSFER_FAILURE:
    case FORM_GET_TEMPLATE_REQUEST:
    case FORM_GET_TEMPLATE_FAILURE:
    case FORM_CLOSE_ERROR:
    case FORM_CLOSE_OUTDATED:
    case FORM_COMPLETE_REQUEST:
    case FORM_COMPLETE_SUCCESS:
    case FORMS_DELETE_REQUEST:
    case FORMS_DELETE_SUCCESS:
    case FORMS_DELETE_FAILURE:
    case FORM_METADATA_REQUEST:
    case FORM_METADATA_SUCCESS:
    case FORM_METADATA_FAILURE:
    case FORM_GET_PUBLICSHARE_IMAGE_URL_REQUEST:
    case FORM_GET_PUBLICSHARE_IMAGE_URL_SUCCESS:
    case FORM_GET_PUBLICSHARE_IMAGE_URL_FAILURE:
    case FORM_RECOVER:
    case FORM_SET_HIGHLIGHT:
    case FORM_SET_VIEW:
    case PUBLIC_FORM_SAVE_REQUEST:
    case PUBLIC_FORM_SAVE_SUCCESS:
    case PUBLIC_FORM_SAVE_FAILURE:
      return mergeDeep(state, action.payload);

    case FORM_INVALID_FIELD_VALUES:
    case FORM_COMPLETE_FIELDS_REQUIRED:
      const form = { ...state, ...action.payload };
      form.sessionId = state.sessionId;
      form.trackingId = localStorage.getItem('trackingId');
      return form;

    case FORM_SAVE_SUCCESS:
      return formSaveSuccess(state, action.payload);

    case UPDATE_SAVED_PUBLIC_FORM:
      return updateSavedPublicFormState(state, action.payload);

    case UPDATE_SAVED_PUBLIC_FORM_BLOB:
      return updateSavedPublicFormBlobState(state, action.payload);

    case UPDATE_SESSION_SIGNATURE:
      if (state.savedSignatures === undefined) {
        state.savedSignatures = {};
      }
      if (action.fieldType === FieldTypes.eSignature) {
        state.savedSignatures.signature = { ...state.savedSignatures.signature, ...action.payload };
      } else {
        state.savedSignatures.initials = { ...state.savedSignatures.initials, ...action.payload };
      }
      return state;

    case FORM_RESET_HIGHLIGHT:
      state.highlight = null;
      return state;

    case FORM_HIPAA_INIT:
      state.hipaaForm = action.payload;
      return state;

    case FORM_HIPAA_CLOSE:
      state.hipaaForm = null;
      return state;

    default:
      // todo - move hipaa into its own reducer
      state = sendAFormReduxReducer(state, action);
      return state;
  }
}

export default formeditor;
